import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiArrowLeft, FiSave } from 'react-icons/fi'
import adminApi from '../../api/adminApi'
import FormInput from '../../components/FormInput/FormInput'
import Select from '../../components/Select/Select'
import Button from '../../components/Button/Button'
import './CreateBatch.css'

const CreateBatch = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [courses, setCourses] = useState([])
  const [formData, setFormData] = useState({
    batchName: '',
    course: '',
    startDate: '',
    endDate: '',
    status: 'upcoming',
  })
  const [selectedCourse, setSelectedCourse] = useState(null)

  useEffect(() => {
    loadCourses()
  }, [])

  const loadCourses = async () => {
    try {
      const response = await adminApi.get('/courses', { params: { limit: 100 } })
      const coursesList = response.data?.courses || response.data?.data || []
      setCourses(coursesList)
    } catch (err) {
      console.error('Failed to load courses:', err)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      const response = await adminApi.post('/batches', formData)
      navigate(`/batches/${response.data._id}`)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create batch')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="create-batch-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Batch Management</p>
          <h1>Create New Batch</h1>
          <p className="subhead">Create a new batch for a course</p>
        </div>
        <Button variant="secondary" onClick={() => navigate('/batches')}>
          <FiArrowLeft size={18} />
          Cancel
        </Button>
      </header>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="create-form">
        <div className="form-section">
          <h2>Batch Information</h2>
          <FormInput
            label="Batch Name"
            name="batchName"
            value={formData.batchName}
            onChange={(e) => setFormData({ ...formData, batchName: e.target.value })}
            placeholder="e.g., Batch 2024-01"
            required
          />
          <Select
            label="Course *"
            name="course"
            value={formData.course}
            onChange={(e) => {
              const courseId = e.target.value
              const course = courses.find(c => c._id === courseId)
              setSelectedCourse(course || null)
              setFormData({ ...formData, course: courseId })
            }}
            options={courses.map((c) => ({ 
              value: c._id, 
              label: `${c.title}${c.code ? ` (${c.code})` : ''}` 
            }))}
            required
            placeholder="Select a course"
          />
          {selectedCourse && (
            <div className="course-info-card">
              <div className="course-info-header">
                <h3>Selected Course</h3>
                <span className="course-badge">Course</span>
              </div>
              <div className="course-info-content">
                <div className="course-info-item">
                  <strong>Course Name:</strong>
                  <span>{selectedCourse.title}</span>
                </div>
                {selectedCourse.code && (
                  <div className="course-info-item">
                    <strong>Course Code:</strong>
                    <span>{selectedCourse.code}</span>
                  </div>
                )}
                {selectedCourse.description && (
                  <div className="course-info-item">
                    <strong>Description:</strong>
                    <span>{selectedCourse.description}</span>
                  </div>
                )}
                {selectedCourse.price && (
                  <div className="course-info-item">
                    <strong>Price:</strong>
                    <span>₹{selectedCourse.price}</span>
                  </div>
                )}
              </div>
              <div className="course-info-footer">
                <p>✓ This batch will be created for the selected course above</p>
              </div>
            </div>
          )}
          <FormInput
            label="Start Date"
            name="startDate"
            type="date"
            value={formData.startDate}
            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            required
          />
          <FormInput
            label="End Date"
            name="endDate"
            type="date"
            value={formData.endDate}
            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
          />
          <Select
            label="Status"
            name="status"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            options={[
              { value: 'upcoming', label: 'Upcoming' },
              { value: 'ongoing', label: 'Ongoing' },
              { value: 'completed', label: 'Completed' },
            ]}
          />
        </div>

        <div className="form-actions">
          <Button variant="secondary" type="button" onClick={() => navigate('/batches')}>
            Cancel
          </Button>
          <Button type="submit" loading={loading}>
            <FiSave size={18} />
            Create Batch
          </Button>
        </div>
      </form>
    </div>
  )
}

export default CreateBatch

